import './PruebaSofia.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useUser } from '../context/UserContext';
import {
  getSeguimientoEjecutivoAlertas,
  getSeguimientoEjecutivoCharts,
  getSeguimientoEjecutivoKpis,
  getSeguimientoEjecutivoTables,
} from '../api/apiConfig';
import SeguimientoEjecutivoAlertasModal from '../components/SeguimientoEjecutivoAlertasModal';

const DEFAULT_FILTERS = {
  plataforma: 'Todos',
  ejecutivo: 'Todos',
};

const DEFAULT_FILTER_OPTIONS = {
  plataforma: ['Todos'],
  ejecutivo: ['Todos'],
};

const filterDefs = [
  { key: 'plataforma', label: 'Plataforma' },
  { key: 'ejecutivo', label: 'Ejecutivo' },
];

const currentMonth = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${month}`;
};

const fmtInt = (v) => Number(v || 0).toLocaleString('es-CL');
const fmtPct = (v) => `${(Number(v || 0) * 100).toLocaleString('es-CL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const fmtPctRaw = (v) => `${Number(v || 0).toLocaleString('es-CL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const fmtMoneyMM = (v) => {
  const value = Number(v || 0) / 1_000_000;
  return `$${value.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} MM`;
};
const fmtMoneyOrSinMeta = (v, sinMeta = false) => (sinMeta ? 'Sin meta' : fmtMoneyMM(v));

const fmtDateTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('es-CL');
};

const kpiValue = (kpi) => {
  const id = String(kpi?.id || '');
  if (id.includes('_pct') || id.includes('gestion_cartera')) return fmtPct(kpi?.value);
  if (id.includes('volumen')) return fmtMoneyMM(kpi?.value);
  return fmtInt(kpi?.value);
};

const kpiNote = (kpi) => {
  const note = Number(kpi?.note);
  if (Number.isFinite(note) && note >= 0 && note <= 1) return fmtPct(note);
  return kpi?.note || '';
};

const INITIAL_VISIBLE_ROWS = 5;

/** Valor numérico para Excel (null si no aplica). */
const excelNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const EXCEL_FMT_INT = '#,##0';
const EXCEL_FMT_PCT = '0.0%';
const EXCEL_FMT_CLP = '#,##0';

const applySheetColumnFormats = (ws, rows, formatsByHeader) => {
  if (!rows?.length || !ws['!ref']) return;
  const headers = Object.keys(rows[0]);
  const range = XLSX.utils.decode_range(ws['!ref']);

  headers.forEach((header, colIndex) => {
    const fmt = formatsByHeader[header];
    if (!fmt) return;
    for (let r = range.s.r + 1; r <= range.e.r; r += 1) {
      const ref = XLSX.utils.encode_cell({ r, c: colIndex });
      const cell = ws[ref];
      if (!cell || cell.v === null || cell.v === undefined || cell.v === '') continue;
      cell.t = 'n';
      cell.z = fmt;
    }
  });
};

const jsonToSheetWithFormats = (rows, formatsByHeader) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  applySheetColumnFormats(ws, rows, formatsByHeader);
  return ws;
};

const RANKING_EXCEL_FORMATS = {
  'Clientes Asignados': EXCEL_FMT_INT,
  'Clientes Gestionados': EXCEL_FMT_INT,
  '% Gestión': EXCEL_FMT_PCT,
  'Clientes Operando': EXCEL_FMT_INT,
  '% Conversión': EXCEL_FMT_PCT,
  'Sin meta': EXCEL_FMT_INT,
  'Volumen Asignado (CLP)': EXCEL_FMT_CLP,
  'Volumen Real (CLP)': EXCEL_FMT_CLP,
  'Brecha (CLP)': EXCEL_FMT_CLP,
};

const RESUMEN_EXCEL_FORMATS = {
  Clientes: EXCEL_FMT_INT,
  '% Gestión': EXCEL_FMT_PCT,
  Operando: EXCEL_FMT_INT,
  'Volumen Asignado (CLP)': EXCEL_FMT_CLP,
  'Volumen Real (CLP)': EXCEL_FMT_CLP,
  '% Captura': EXCEL_FMT_PCT,
};

const buildRankingExportRows = (rows, total) => {
  const mapRow = (r) => ({
    Plataforma: r.plataforma || '',
    Ejecutivo: r.ejecutivo || '',
    'Clientes Asignados': excelNum(r.clientes_asignados),
    'Clientes Gestionados': excelNum(r.clientes_gestionados),
    '% Gestión': excelNum(r.pct_gestion),
    'Clientes Operando': excelNum(r.clientes_operando),
    '% Conversión': excelNum(r.pct_conversion),
    'Sin meta': r.sin_meta ? 1 : 0,
    'Volumen Asignado (CLP)': r.sin_meta ? null : excelNum(r.volumen_asignado),
    'Volumen Real (CLP)': excelNum(r.volumen_real),
    'Brecha (CLP)': excelNum(r.brecha),
  });

  const sheetRows = rows.map(mapRow);

  if (total) {
    const brechaTotal = Number(total.volumen_real || 0) - Number(total.volumen_asignado || 0);
    sheetRows.push({
      Plataforma: 'TOTAL GENERAL',
      Ejecutivo: '',
      'Clientes Asignados': excelNum(total.clientes_asignados),
      'Clientes Gestionados': excelNum(total.clientes_gestionados),
      '% Gestión': excelNum(total.pct_gestion),
      'Clientes Operando': excelNum(total.clientes_operando),
      '% Conversión': excelNum(total.pct_conversion),
      'Sin meta': 0,
      'Volumen Asignado (CLP)': excelNum(total.volumen_asignado),
      'Volumen Real (CLP)': excelNum(total.volumen_real),
      'Brecha (CLP)': excelNum(brechaTotal),
    });
  }

  return sheetRows;
};

const buildResumenExportRows = (rows, total) => {
  const sheetRows = rows.map((r) => ({
    Plataforma: r.plataforma || '',
    Clientes: excelNum(r.clientes),
    '% Gestión': excelNum(r.pct_gestion),
    Operando: excelNum(r.operando),
    'Volumen Asignado (CLP)': excelNum(r.volumen_asignado),
    'Volumen Real (CLP)': excelNum(r.volumen_real),
    '% Captura': excelNum(r.pct_captura),
  }));

  if (total) {
    sheetRows.push({
      Plataforma: 'Total General',
      Clientes: excelNum(total.clientes),
      '% Gestión': excelNum(total.pct_gestion),
      Operando: excelNum(total.operando),
      'Volumen Asignado (CLP)': excelNum(total.volumen_asignado),
      'Volumen Real (CLP)': excelNum(total.volumen_real),
      '% Captura': excelNum(total.pct_captura),
    });
  }

  return sheetRows;
};

const sortRows = (rows, sortConfig) => {
  if (!sortConfig?.key) return rows;
  const { key, direction } = sortConfig;
  const dir = direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const av = a?.[key];
    const bv = b?.[key];

    const aNum = Number(av);
    const bNum = Number(bv);
    const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);
    if (bothNumeric) return (aNum - bNum) * dir;

    return String(av ?? '').localeCompare(String(bv ?? ''), 'es', { sensitivity: 'base' }) * dir;
  });
};

const PruebaSofia = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [month, setMonth] = useState(currentMonth());
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);
  const [rankingSearch, setRankingSearch] = useState('');
  const [resumenSearch, setResumenSearch] = useState('');
  const [rankingSort, setRankingSort] = useState({ key: null, direction: 'desc' });
  const [resumenSort, setResumenSort] = useState({ key: null, direction: 'desc' });
  const [expanded, setExpanded] = useState({
    gestion: false,
    volumen: false,
    ranking: false,
    resumen: false,
  });

  const [kpis, setKpis] = useState([]);
  const [charts, setCharts] = useState({
    gestion_por_ejecutivo: [],
    volumen_por_ejecutivo: [],
    gestion_vs_resultado: { points: [], legend: [] },
  });
  const [tables, setTables] = useState({
    ranking: { rows: [], total: null },
    resumen_plataforma: { rows: [], total: null },
  });
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState('');
  const [alertTypes, setAlertTypes] = useState([]);
  const [alertsTotalCount, setAlertsTotalCount] = useState(0);

  const kpisVisible = useMemo(() => {
    const volumenCapturado = kpis.find((k) => k?.id === 'volumen_capturado_pct');
    const gestionCartera = kpis.find((k) => k?.id === 'gestion_cartera_pct');
    return kpis
      .filter((k) => k?.id !== 'volumen_capturado_pct' && k?.id !== 'gestion_cartera_pct')
      .map((k) => {
        if (k?.id === 'clientes_gestionados' && gestionCartera) {
          return {
            ...k,
            note: gestionCartera.note || 'Avance de gestión',
            extraLine: `% Gestión Cartera: ${fmtPct(gestionCartera.value)}`,
          };
        }
        if (k?.id === 'volumen_real' && volumenCapturado) {
          return {
            ...k,
            extraLine: `% Volumen Capturado: ${fmtPct(volumenCapturado.value)}`,
          };
        }
        return k;
      });
  }, [kpis]);

  const requestPayload = useMemo(() => {
    const payload = { month };
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'Todos') payload[key] = value;
    });
    return payload;
  }, [month, filters]);

  const cargarAlertas = useCallback(async () => {
    if (!user) return;
    setAlertsLoading(true);
    setAlertsError('');
    try {
      const resp = await getSeguimientoEjecutivoAlertas(user, requestPayload);
      if (!resp?.ok) throw new Error(resp?.error || 'No se pudieron cargar las alertas.');
      setAlertTypes(Array.isArray(resp?.data?.alert_types) ? resp.data.alert_types : []);
      setAlertsTotalCount(Number(resp?.data?.total_count || 0));
    } catch (e) {
      console.error(e);
      setAlertsError(e?.message || 'Error cargando alertas.');
      setAlertTypes([]);
      setAlertsTotalCount(0);
    } finally {
      setAlertsLoading(false);
    }
  }, [user, requestPayload]);

  const cargarDashboard = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [kpisResp, chartsResp, tablesResp, alertasResp] = await Promise.all([
        getSeguimientoEjecutivoKpis(user, requestPayload),
        getSeguimientoEjecutivoCharts(user, requestPayload),
        getSeguimientoEjecutivoTables(user, requestPayload),
        getSeguimientoEjecutivoAlertas(user, requestPayload),
      ]);

      if (!kpisResp?.ok) throw new Error(kpisResp?.error || 'No se pudieron cargar los KPIs.');
      if (!chartsResp?.ok) throw new Error(chartsResp?.error || 'No se pudieron cargar los gráficos.');
      if (!tablesResp?.ok) throw new Error(tablesResp?.error || 'No se pudieron cargar las tablas.');

      if (alertasResp?.ok) {
        setAlertTypes(Array.isArray(alertasResp?.data?.alert_types) ? alertasResp.data.alert_types : []);
        setAlertsTotalCount(Number(alertasResp?.data?.total_count || 0));
        setAlertsError('');
      } else {
        setAlertTypes([]);
        setAlertsTotalCount(0);
      }

      setKpis(Array.isArray(kpisResp?.data?.kpis) ? kpisResp.data.kpis : []);
      setCharts({
        gestion_por_ejecutivo: Array.isArray(chartsResp?.data?.gestion_por_ejecutivo) ? chartsResp.data.gestion_por_ejecutivo : [],
        volumen_por_ejecutivo: Array.isArray(chartsResp?.data?.volumen_por_ejecutivo) ? chartsResp.data.volumen_por_ejecutivo : [],
        gestion_vs_resultado: chartsResp?.data?.gestion_vs_resultado || { points: [], legend: [] },
      });
      setTables({
        ranking: tablesResp?.data?.ranking || { rows: [], total: null },
        resumen_plataforma: tablesResp?.data?.resumen_plataforma || { rows: [], total: null },
      });

      const nextOptions = tablesResp?.data?.filter_options || {};
      setFilterOptions({
        plataforma: ['Todos', ...(nextOptions.plataforma || [])],
        ejecutivo: ['Todos', ...(nextOptions.ejecutivo || [])],
      });

      setMeta(tablesResp?.meta || chartsResp?.meta || kpisResp?.meta || null);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Error cargando seguimiento ejecutivo.');
    } finally {
      setLoading(false);
    }
  }, [user, requestPayload]);

  useEffect(() => {
    cargarDashboard();
  }, [cargarDashboard]);

  const abrirAlertas = () => {
    setAlertsOpen(true);
    cargarAlertas();
  };

  const maxVolumen = useMemo(() => {
    const values = charts.volumen_por_ejecutivo.flatMap((r) => {
      const real = Number(r.value_abs || 0);
      const match = (tables?.ranking?.rows || []).find((row) => row.ejecutivo === r.name);
      const meta = match?.sin_meta ? 0 : Number(match?.volumen_asignado || 0);
      return [real, meta];
    });
    const max = Math.max(...values, 0);
    return max > 0 ? max : 1;
  }, [charts.volumen_por_ejecutivo, tables]);

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSort = (table, key) => {
    if (table === 'ranking') {
      setRankingSort((prev) => {
        if (prev.key === key) {
          return { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
        }
        return { key, direction: 'desc' };
      });
      return;
    }
    setResumenSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const compactVolumenRows = useMemo(
    () => charts.volumen_por_ejecutivo.slice(0, INITIAL_VISIBLE_ROWS),
    [charts.volumen_por_ejecutivo],
  );

  const compactGestionRows = useMemo(() => {
    const base = charts.gestion_por_ejecutivo || [];
    if (!compactVolumenRows.length) return base.slice(0, INITIAL_VISIBLE_ROWS);

    // El panel derecho ocupa más alto por tener dos barras por ejecutivo (real/meta).
    // Mostramos más filas en el izquierdo para rellenar visualmente el mismo bloque.
    const targetRows = Math.min(base.length, compactVolumenRows.length * 2);
    return base.slice(0, targetRows);
  }, [charts.gestion_por_ejecutivo, compactVolumenRows]);

  const gestionRowsVisible = expanded.gestion
    ? charts.gestion_por_ejecutivo
    : compactGestionRows;

  const volumenRowsVisible = expanded.volumen
    ? charts.volumen_por_ejecutivo
    : compactVolumenRows;

  const rankingRowsProcessed = useMemo(() => {
    const rows = (tables?.ranking?.rows || []).map((r) => {
      const volumenAsignado = Number(r?.volumen_asignado || 0);
      const volumenReal = Number(r?.volumen_real || 0);
      const brecha = volumenReal - volumenAsignado;
      return {
        ...r,
        brecha,
        brecha_tone: brecha < 0 ? 'negative' : brecha > 0 ? 'positive' : 'neutral',
      };
    });
    const q = rankingSearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => (
        `${r.plataforma} ${r.ejecutivo} ${r.clientes_asignados} ${r.clientes_gestionados} ${r.clientes_operando}`
      ).toLowerCase().includes(q))
      : rows;
    return sortRows(filtered, rankingSort);
  }, [tables?.ranking?.rows, rankingSearch, rankingSort]);

  const resumenRowsProcessed = useMemo(() => {
    const rows = tables?.resumen_plataforma?.rows || [];
    const q = resumenSearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => (
        `${r.plataforma} ${r.clientes} ${r.operando}`
      ).toLowerCase().includes(q))
      : rows;
    return sortRows(filtered, resumenSort);
  }, [tables?.resumen_plataforma?.rows, resumenSearch, resumenSort]);

  const rankingRowsVisible = expanded.ranking
    ? rankingRowsProcessed
    : rankingRowsProcessed.slice(0, INITIAL_VISIBLE_ROWS);

  const resumenRowsVisible = expanded.resumen
    ? resumenRowsProcessed
    : resumenRowsProcessed.slice(0, INITIAL_VISIBLE_ROWS);

  const exportarTablasExcel = useCallback((options = { ranking: true, resumen: true }) => {
    const { ranking = true, resumen = true } = options;
    const wb = XLSX.utils.book_new();
    let hasSheets = false;

    if (ranking && rankingRowsProcessed.length > 0) {
      const rankingData = buildRankingExportRows(
        rankingRowsProcessed,
        tables?.ranking?.total,
      );
      XLSX.utils.book_append_sheet(
        wb,
        jsonToSheetWithFormats(rankingData, RANKING_EXCEL_FORMATS),
        'Ranking Ejecutivos',
      );
      hasSheets = true;
    }

    if (resumen && resumenRowsProcessed.length > 0) {
      const resumenData = buildResumenExportRows(
        resumenRowsProcessed,
        tables?.resumen_plataforma?.total,
      );
      XLSX.utils.book_append_sheet(
        wb,
        jsonToSheetWithFormats(resumenData, RESUMEN_EXCEL_FORMATS),
        'Resumen Plataforma',
      );
      hasSheets = true;
    }

    if (!hasSheets) return;

    const filterSuffix = [
      filters.plataforma !== 'Todos' ? filters.plataforma : null,
      filters.ejecutivo !== 'Todos' ? filters.ejecutivo : null,
    ]
      .filter(Boolean)
      .join('_');
    const fileName = filterSuffix
      ? `seguimiento_ejecutivos_${month}_${filterSuffix}.xlsx`
      : `seguimiento_ejecutivos_${month}.xlsx`;

    XLSX.writeFile(wb, fileName);
  }, [
    rankingRowsProcessed,
    resumenRowsProcessed,
    tables?.ranking?.total,
    tables?.resumen_plataforma?.total,
    month,
    filters.plataforma,
    filters.ejecutivo,
  ]);

  const puedeExportarTablas = rankingRowsProcessed.length > 0 || resumenRowsProcessed.length > 0;

  const scopeHint = useMemo(() => {
    const scope = meta?.access_scope;
    if (!scope?.allowed) return null;
    if (scope.scope === 'equipo') {
      const equipos = Array.isArray(scope.equipos) ? scope.equipos.filter(Boolean) : [];
      if (equipos.length > 1) {
        return `Vista restringida a tus equipos (${equipos.join(', ')}).`;
      }
      return `Vista de líder de equipo${scope.grupo ? ` (${scope.grupo})` : ''}: solo ejecutivos de tu equipo.`;
    }
    if (scope.scope === 'all') {
      return 'Vista gerencial: todos los equipos y ejecutivos.';
    }
    return null;
  }, [meta]);

  return (
    <div className="ps-page-wrap">
      <button
        type="button"
        className="ps-back-hidden-btn"
        title="Volver"
        onClick={() => navigate(-1)}
      >
        ←
      </button>
      <div className="ps-app">
        <main className="ps-main">
          <header className="ps-topbar">
            <div>
              <h1>Seguimiento Ejecutivo de Cartera Comercial</h1>
              <p className="ps-subtitle">Control de gestión de cartera, clientes y volumen por ejecutivo y plataforma.</p>
            </div>
            <div className="ps-actions">
              <button
                className="ps-export-btn ps-alerts-btn"
                type="button"
                onClick={abrirAlertas}
                disabled={loading}
                title="Ver alertas de gestión cartera (operando, en gestión arrastrado, compromete sin operar)"
              >
                Alertas
                {alertsTotalCount > 0 && (
                  <span className="ps-alerts-badge">{alertsTotalCount}</span>
                )}
              </button>
              <button
                className="ps-export-btn"
                type="button"
                onClick={() => exportarTablasExcel()}
                disabled={loading || !puedeExportarTablas}
                title="Descarga las tablas con valores numéricos listos para fórmulas en Excel"
              >
                Exportar Excel
              </button>
              <input
                className="ps-date-picker"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
              <button className="ps-export-btn" type="button" onClick={cargarDashboard} disabled={loading}>
                {loading ? 'Cargando...' : 'Recargar'}
              </button>
            </div>
          </header>

          {loading && (
            <section className="ps-loading-card" aria-live="polite">
              <div className="ps-loading-title">Cargando datos del tablero...</div>
              <div className="ps-loading-track">
                <div className="ps-loading-fill" />
              </div>
            </section>
          )}

          {error && <div className="ps-alert-error">{error}</div>}

          {scopeHint && !error && (
            <div className="ps-scope-hint" role="status">
              {scopeHint}
            </div>
          )}

          <section className="ps-kpi-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                <article className="ps-card ps-kpi-card ps-kpi-skeleton" key={`kpi-skeleton-${i}`}>
                  <div className="ps-kpi-icon skeleton-box" />
                  <div className="ps-kpi-skeleton-lines">
                    <div className="skeleton-line w60" />
                    <div className="skeleton-line w40" />
                    <div className="skeleton-line w50" />
                  </div>
                </article>
              ))
              : kpisVisible.map((kpi) => (
              <article className="ps-card ps-kpi-card" key={kpi.id}>
                <div className={`ps-kpi-icon ${kpi.tone}`}>{kpi.icon}</div>
                <div>
                  <div className="ps-kpi-title">{kpi.title}</div>
                  <div className="ps-kpi-value">{kpiValue(kpi)}</div>
                  <div className="ps-kpi-note">{kpiNote(kpi)}</div>
                  {kpi.extraLine && <div className="ps-kpi-extra">{kpi.extraLine}</div>}
                </div>
              </article>
              ))}
          </section>

          <section className="ps-card ps-filters">
            {filterDefs.map((f) => (
              <div className="ps-filter" key={f.key}>
                <label>{f.label}</label>
                <select value={filters[f.key]} onChange={(e) => onFilterChange(f.key, e.target.value)}>
                  {(filterOptions[f.key] || ['Todos']).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </section>

          <section className="ps-charts-grid">
            <article className="ps-card ps-panel">
              <div className="ps-panel-header">
                <h2 className="ps-panel-title">% de Clientes Gestionados por Ejecutivo</h2>
                {charts.gestion_por_ejecutivo.length > INITIAL_VISIBLE_ROWS && (
                  <button type="button" className="ps-show-more-btn ps-show-more-btn-top" onClick={() => toggleExpanded('gestion')}>
                    {expanded.gestion ? 'Ver menos' : 'Ver más'}
                  </button>
                )}
              </div>
              {loading ? Array.from({ length: 8 }).map((_, i) => (
                <div className="ps-bar-row ps-row-skeleton" key={`gestion-skeleton-${i}`}>
                  <span className="skeleton-line w50" />
                  <div className="ps-bar-track">
                    <div className="skeleton-fill" />
                  </div>
                </div>
              )) : gestionRowsVisible.map((r) => (
                <div className="ps-bar-row" key={r.name}>
                  <span>{r.name}</span>
                  <div className="ps-bar-track">
                    <div className="ps-bar-fill" style={{ width: `${Math.max(0, Math.min(100, Number(r.value_pct || 0)))}%` }}>
                      <span className="ps-bar-fill-label">{fmtPctRaw(r.value_pct)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {charts.gestion_por_ejecutivo.length > INITIAL_VISIBLE_ROWS && !loading && (
                <button type="button" className="ps-show-more-btn" onClick={() => toggleExpanded('gestion')}>
                  {expanded.gestion ? 'Ver menos' : 'Ver más'}
                </button>
              )}
            </article>

            <article className="ps-card ps-panel">
              <div className="ps-panel-header">
                <h2 className="ps-panel-title">Volumen Operado vs Meta por Ejecutivo</h2>
                {charts.volumen_por_ejecutivo.length > INITIAL_VISIBLE_ROWS && (
                  <button type="button" className="ps-show-more-btn ps-show-more-btn-top" onClick={() => toggleExpanded('volumen')}>
                    {expanded.volumen ? 'Ver menos' : 'Ver más'}
                  </button>
                )}
              </div>
              {loading ? Array.from({ length: 5 }).map((_, i) => (
                <div className="ps-volume-compare-row ps-row-skeleton" key={`volumen-skeleton-${i}`}>
                  <div className="ps-volume-compare-header">
                    <span className="skeleton-line w40" />
                  </div>
                  <div className="ps-volume-bar-line">
                    <small>Real</small>
                    <div className="ps-bar-track"><div className="skeleton-fill" /></div>
                  </div>
                  <div className="ps-volume-bar-line">
                    <small>Meta</small>
                    <div className="ps-bar-track"><div className="skeleton-fill" /></div>
                  </div>
                </div>
              )) : volumenRowsVisible.map((r) => {
                const rankingRow = (tables?.ranking?.rows || []).find((row) => row.ejecutivo === r.name);
                const sinMeta = Boolean(rankingRow?.sin_meta);
                const metaValue = sinMeta ? 0 : Number(rankingRow?.volumen_asignado || 0);
                const realWidthPct = Math.max(0, Math.min(100, (Number(r.value_abs || 0) / maxVolumen) * 100));
                const metaWidthPct = Math.max(0, Math.min(100, (metaValue / maxVolumen) * 100));
                return (
                <div className="ps-volume-compare-row" key={r.name}>
                  <div className="ps-volume-compare-header">
                    <span>{r.name}</span>
                  </div>
                  <div className="ps-volume-bar-line">
                    <small>Real</small>
                    <div className="ps-bar-track">
                      <div
                        className="ps-bar-fill ps-bar-fill-real"
                        style={{ width: `${realWidthPct}%` }}
                      >
                        <span className={`ps-bar-fill-label ${realWidthPct < 14 ? 'outside' : ''}`}>{fmtMoneyMM(r.value_abs)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ps-volume-bar-line">
                    <small>Meta</small>
                    <div className="ps-bar-track">
                      <div
                        className="ps-bar-fill ps-bar-fill-meta"
                        style={{ width: `${metaWidthPct}%` }}
                      >
                        <span className={`ps-bar-fill-label ${metaWidthPct < 14 || sinMeta ? 'outside' : ''} ${sinMeta ? 'ps-no-meta-label' : ''}`}>
                          {fmtMoneyOrSinMeta(metaValue, sinMeta)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )})}
              {charts.volumen_por_ejecutivo.length > INITIAL_VISIBLE_ROWS && !loading && (
                <button type="button" className="ps-show-more-btn" onClick={() => toggleExpanded('volumen')}>
                  {expanded.volumen ? 'Ver menos' : 'Ver más'}
                </button>
              )}
            </article>
          </section>

          <section className="ps-tables-grid">
            <article className="ps-card ps-panel ps-table-scroll">
              <div className="ps-panel-header">
                <h2 className="ps-panel-title">Ranking de Ejecutivos</h2>
                <div className="ps-table-header-actions">
                  <input
                    className="ps-table-search"
                    placeholder="Buscar en ranking..."
                    value={rankingSearch}
                    onChange={(e) => setRankingSearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="ps-export-btn ps-export-btn-compact"
                    onClick={() => exportarTablasExcel({ ranking: true, resumen: false })}
                    disabled={loading || rankingRowsProcessed.length === 0}
                    title="Descargar ranking en Excel"
                  >
                    Excel
                  </button>
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('ranking', 'plataforma')}>Plat.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('ranking', 'ejecutivo')}>Ejecutivo</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('ranking', 'clientes_asignados')}>Asig.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('ranking', 'clientes_gestionados')}>Gest.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('ranking', 'pct_gestion')}>% Gest.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('ranking', 'clientes_operando')}>Oper.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('ranking', 'pct_conversion')}>% Conv.</button></th>
                    <th className="ps-money"><button type="button" className="ps-sort-btn ps-sort-btn-right" onClick={() => toggleSort('ranking', 'volumen_asignado')}>Vol. Asig.</button></th>
                    <th className="ps-money"><button type="button" className="ps-sort-btn ps-sort-btn-right" onClick={() => toggleSort('ranking', 'volumen_real')}>Vol. Real</button></th>
                    <th className="ps-money"><button type="button" className="ps-sort-btn ps-sort-btn-right" onClick={() => toggleSort('ranking', 'brecha')}>Brecha</button></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? Array.from({ length: 7 }).map((_, i) => (
                    <tr key={`ranking-skeleton-${i}`}>
                      <td colSpan={10}><div className="skeleton-line w100" /></td>
                    </tr>
                  )) : rankingRowsVisible.map((r) => (
                    <tr key={`${r.plataforma}-${r.ejecutivo}`}>
                      <td>{r.plataforma}</td>
                      <td>{r.ejecutivo}</td>
                      <td>{fmtInt(r.clientes_asignados)}</td>
                      <td>{fmtInt(r.clientes_gestionados)}</td>
                      <td>{fmtPct(r.pct_gestion)}</td>
                      <td>{fmtInt(r.clientes_operando)}</td>
                      <td>{fmtPct(r.pct_conversion)}</td>
                      <td className={`ps-money ${r.sin_meta ? 'ps-no-meta-label' : ''}`}>{fmtMoneyOrSinMeta(r.volumen_asignado, r.sin_meta)}</td>
                      <td className="ps-money">{fmtMoneyMM(r.volumen_real)}</td>
                      <td className={`ps-money ${
                        Number(r.brecha || 0) < 0 ? 'ps-negative' : Number(r.brecha || 0) > 0 ? 'ps-positive' : ''
                      }`}>
                        {fmtMoneyMM(r.brecha)}
                      </td>
                    </tr>
                  ))}
                  {tables?.ranking?.total && !loading && (
                    <tr className="ps-total-row">
                      <td colSpan={2}>TOTAL GENERAL</td>
                      <td>{fmtInt(tables.ranking.total.clientes_asignados)}</td>
                      <td>{fmtInt(tables.ranking.total.clientes_gestionados)}</td>
                      <td>{fmtPct(tables.ranking.total.pct_gestion)}</td>
                      <td>{fmtInt(tables.ranking.total.clientes_operando)}</td>
                      <td>{fmtPct(tables.ranking.total.pct_conversion)}</td>
                      <td className="ps-money">{fmtMoneyMM(tables.ranking.total.volumen_asignado)}</td>
                      <td className="ps-money">{fmtMoneyMM(tables.ranking.total.volumen_real)}</td>
                      <td className={`ps-money ${
                        (Number(tables.ranking.total.volumen_real || 0) - Number(tables.ranking.total.volumen_asignado || 0)) < 0
                          ? 'ps-negative'
                          : 'ps-positive'
                      }`}
                      >
                        {fmtMoneyMM(Number(tables.ranking.total.volumen_real || 0) - Number(tables.ranking.total.volumen_asignado || 0))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {rankingRowsProcessed.length > INITIAL_VISIBLE_ROWS && !loading && (
                <button type="button" className="ps-show-more-btn" onClick={() => toggleExpanded('ranking')}>
                  {expanded.ranking ? 'Ver menos' : 'Ver más'}
                </button>
              )}
            </article>

            <article className="ps-card ps-panel ps-table-scroll">
              <div className="ps-panel-header">
                <h2 className="ps-panel-title">Resumen por Plataforma</h2>
                <div className="ps-table-header-actions">
                  <input
                    className="ps-table-search"
                    placeholder="Buscar en resumen..."
                    value={resumenSearch}
                    onChange={(e) => setResumenSearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="ps-export-btn ps-export-btn-compact"
                    onClick={() => exportarTablasExcel({ ranking: false, resumen: true })}
                    disabled={loading || resumenRowsProcessed.length === 0}
                    title="Descargar resumen por plataforma en Excel"
                  >
                    Excel
                  </button>
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('resumen', 'plataforma')}>Plat.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('resumen', 'clientes')}>Cli.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('resumen', 'pct_gestion')}>% Gest.</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('resumen', 'operando')}>Oper.</button></th>
                    <th className="ps-money"><button type="button" className="ps-sort-btn ps-sort-btn-right" onClick={() => toggleSort('resumen', 'volumen_asignado')}>Vol. Asig.</button></th>
                    <th className="ps-money"><button type="button" className="ps-sort-btn ps-sort-btn-right" onClick={() => toggleSort('resumen', 'volumen_real')}>Vol. Real</button></th>
                    <th><button type="button" className="ps-sort-btn" onClick={() => toggleSort('resumen', 'pct_captura')}>% Cap.</button></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? Array.from({ length: 7 }).map((_, i) => (
                    <tr key={`resumen-skeleton-${i}`}>
                      <td colSpan={7}><div className="skeleton-line w100" /></td>
                    </tr>
                  )) : resumenRowsVisible.map((r) => (
                    <tr key={r.plataforma}>
                      <td>{r.plataforma}</td>
                      <td>{fmtInt(r.clientes)}</td>
                      <td>{fmtPct(r.pct_gestion)}</td>
                      <td>{fmtInt(r.operando)}</td>
                      <td className="ps-money">{fmtMoneyMM(r.volumen_asignado)}</td>
                      <td className="ps-money">{fmtMoneyMM(r.volumen_real)}</td>
                      <td className="ps-positive">{fmtPct(r.pct_captura)}</td>
                    </tr>
                  ))}
                  {tables?.resumen_plataforma?.total && !loading && (
                    <tr className="ps-total-row">
                      <td>Total General</td>
                      <td>{fmtInt(tables.resumen_plataforma.total.clientes)}</td>
                      <td>{fmtPct(tables.resumen_plataforma.total.pct_gestion)}</td>
                      <td>{fmtInt(tables.resumen_plataforma.total.operando)}</td>
                      <td className="ps-money">{fmtMoneyMM(tables.resumen_plataforma.total.volumen_asignado)}</td>
                      <td className="ps-money">{fmtMoneyMM(tables.resumen_plataforma.total.volumen_real)}</td>
                      <td>{fmtPct(tables.resumen_plataforma.total.pct_captura)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {resumenRowsProcessed.length > INITIAL_VISIBLE_ROWS && !loading && (
                <button type="button" className="ps-show-more-btn" onClick={() => toggleExpanded('resumen')}>
                  {expanded.resumen ? 'Ver menos' : 'Ver más'}
                </button>
              )}
            </article>
          </section>

          <section className="ps-card ps-footer-note">
            <span>Los datos mostrados corresponden al periodo seleccionado y se actualizan diariamente.</span>
            <span>
              Ventana: {meta?.month_start || '-'} a {meta?.month_end_exclusive || '-'} | Actualizado: {fmtDateTime(meta?.generated_at)}
            </span>
          </section>
        </main>
      </div>

      <SeguimientoEjecutivoAlertasModal
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        alertTypes={alertTypes}
        loading={alertsLoading}
        error={alertsError}
        month={month}
      />
    </div>
  );
};

export default PruebaSofia;
